import { designVariables } from "@studio3t/design-variables";
import {
  GenericStyles,
  Icon,
  Themes,
  TopNavigation,
} from "@studio3t/generic-components";
import { type MouseEvent, useCallback, useEffect, useMemo } from "react";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import styled, { ThemeProvider } from "styled-components";
import { AuthCallback, AuthGuard, AuthProvider } from "@/auth";
import { AiChatProvider, useAiChatContext } from "@/components/ai/chat";
import { AiAgentPage } from "@/components/ai/chat/AiAgentPage";
import { BridgeMenu, NAV_ITEMS } from "@/components/BridgeMenu";
import {
  type Command,
  CommandPalette,
  CommandPaletteProvider,
  useCommandPalette,
  useRegisterCommands,
} from "@/components/command-palette";
import {
  LeaveGuardProvider,
  useGuardedNavigate,
} from "@/components/LeaveGuard";
import { LegacyWorkspaceRedirect } from "@/components/LegacyWorkspaceRedirect";
import { LicenseBanner } from "@/components/LicenseBanner";
import { OverlayScroll } from "@/components/OverlayScroll";
import { ToastProvider } from "@/components/toast/ToastProvider";
import { UserMenu } from "@/components/UserMenu";
import { GlobalStyles } from "@/GlobalStyles";
import AddService from "@/pages/AddService";
import AuditLog from "@/pages/AuditLog";
import CreatePipeline from "@/pages/CreatePipeline";
import Dashboard from "@/pages/Dashboard";
import Logs from "@/pages/Logs";
import NewStudio from "@/pages/NewStudio";
import PipelineDetail from "@/pages/PipelineDetail";
import Pipelines from "@/pages/Pipelines";
import ServiceDetail from "@/pages/ServiceDetail";
import Services from "@/pages/Services";

import { isPlainClick } from "@/utils/plainClick";
import { studioPath } from "@/utils/studioRoute";
import { retireStoredKeys } from "@/utils/userStorage";

const SPACING = designVariables.ui.semantic;

const IS_MAC = navigator.platform.toUpperCase().includes("MAC");

const AppContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: ${(p) => p.theme.semanticAppBackground};
  color: ${(p) => p.theme.semanticTextBody};
`;

const Body = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
`;

// The main page scroll uses the app's custom OverlayScrollbars (like the cards
// and tables) rather than the native browser scrollbar. OverlayScrollbars
// manages the overflow, so no `overflow` here.
const PageContainer = styled(OverlayScroll)<{ $noPadding?: boolean }>`
  flex: 1;
  min-width: 0;
  padding: ${(p) =>
    p.$noPadding ? "0" : `${SPACING.spacings}px ${SPACING.spacingl}px`};
  background: ${(p) => p.theme.semanticAppBackground};
`;

function buildNavCommands(navigate: (path: string) => void): Command[] {
  const cmds = NAV_ITEMS.map((item) => ({
    id: `nav:${item.to}`,
    label: `Go to ${item.label}`,
    group: "Navigation",
    // Studio resolves the persisted New Studio choice at execution time.
    action: () => navigate(item.to === "/studio" ? studioPath() : item.to),
  }));
  // AI Agent lives in the top bar rather than the rail, so it isn't in
  // NAV_ITEMS — add an explicit command so keyboard navigation still reaches it.
  cmds.push({
    id: "nav:/agent",
    label: "Go to AI Agent",
    group: "Navigation",
    action: () => navigate("/agent"),
  });
  return cmds;
}

function isAiShortcut(e: KeyboardEvent): boolean {
  const isMod = IS_MAC ? e.metaKey : e.ctrlKey;
  return isMod && e.shiftKey && e.key.toLowerCase() === "a";
}

function useAiShortcut(isStudioPage: boolean, toggleAi: () => void) {
  useEffect(() => {
    if (!isStudioPage) return;

    function handleKeyDown(e: KeyboardEvent) {
      if (isAiShortcut(e)) {
        e.preventDefault();
        toggleAi();
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [toggleAi, isStudioPage]);
}

function useAppCommands(isStudioPage: boolean) {
  const navigate = useNavigate();
  const { showAiSidebar, setShowAiSidebar } = useAiChatContext();
  const { open: openPalette } = useCommandPalette();

  const toggleAi = useCallback(
    () => setShowAiSidebar(!showAiSidebar),
    [showAiSidebar, setShowAiSidebar],
  );

  const commands: Command[] = useMemo(() => {
    const cmds = buildNavCommands(navigate);
    if (isStudioPage) {
      cmds.push({
        id: "ai:toggle",
        label: showAiSidebar ? "Collapse AI Helper" : "Expand AI Helper",
        group: "AI",
        shortcut: IS_MAC ? "⌘⇧A" : "Ctrl+Shift+A",
        action: toggleAi,
      });
    }
    cmds.push({
      id: "palette:open",
      label: "Open Command Palette",
      group: "General",
      shortcut: IS_MAC ? "⌘K" : "Ctrl+K",
      action: openPalette,
    });
    return cmds;
  }, [navigate, showAiSidebar, toggleAi, openPalette, isStudioPage]);

  useRegisterCommands(commands);
  useAiShortcut(isStudioPage, toggleAi);
}

// Stale /newStudio links (bookmarks, the old top-bar toggle) land on the
// one Studio route, params intact.
function NewStudioRedirect() {
  const location = useLocation();
  return (
    <Navigate to={{ pathname: "/studio", search: location.search }} replace />
  );
}

// The top bar's links are plain anchors (the library is router-agnostic):
// a plain click routes in-app behind the leave guard; a modified click or
// a middle click keeps the browser's own behaviour (new tab, new window).
function useTopBarLink() {
  const guardedNavigate = useGuardedNavigate();
  return useCallback(
    (to: string) => (e: MouseEvent<HTMLElement>) => {
      if (!isPlainClick(e)) return;
      e.preventDefault();
      guardedNavigate(to);
    },
    [guardedNavigate],
  );
}

function AppShell() {
  const location = useLocation();
  const isStudioPage = location.pathname.startsWith("/studio");

  // Inside the auth boundary, so the keys go from the signed-in user's
  // storage scope.
  useEffect(retireStoredKeys, []);
  useAppCommands(isStudioPage);
  const topBarLink = useTopBarLink();

  return (
    <AppContainer>
      <TopNavigation label="Top">
        <TopNavigation.Left>
          <TopNavigation.Brand
            icon={Icon.Manatee}
            href="/"
            onClick={topBarLink("/")}
          >
            3TL Bridge
          </TopNavigation.Brand>
        </TopNavigation.Left>
        <TopNavigation.Right>
          <TopNavigation.IconButton
            icon={Icon.Help}
            tooltip="Documentation"
            href="https://docs.3t.io/docs/3tl-bridge"
            external
          />
          <TopNavigation.HighlightButton
            icon={Icon.AI}
            tooltip="AI Agent"
            href="/agent"
            onClick={topBarLink("/agent")}
          >
            Agent
          </TopNavigation.HighlightButton>
          <UserMenu />
        </TopNavigation.Right>
      </TopNavigation>

      <LicenseBanner />

      <Body>
        <BridgeMenu />
        <PageContainer $noPadding={location.pathname === "/agent"}>
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/pipelines" element={<Pipelines />} />
            <Route path="/pipelines/new" element={<CreatePipeline />} />
            <Route path="/pipelines/:name" element={<PipelineDetail />} />
            <Route path="/services" element={<Services />} />
            <Route path="/services/new" element={<AddService />} />
            <Route path="/services/:name" element={<ServiceDetail />} />
            <Route
              path="/reservations"
              element={<Navigate to="/services" replace />}
            />
            <Route path="/studio" element={<NewStudio />} />
            <Route path="/newStudio" element={<NewStudioRedirect />} />
            <Route
              path="/workspaces/:id?"
              element={<LegacyWorkspaceRedirect />}
            />
            <Route path="/logs" element={<Logs />} />
            <Route path="/audit-log" element={<AuditLog />} />
            <Route path="/agent" element={<AiAgentPage />} />
          </Routes>
        </PageContainer>
      </Body>

      <CommandPalette />
    </AppContainer>
  );
}

function App() {
  return (
    <ThemeProvider theme={Themes.light}>
      <GenericStyles />
      <GlobalStyles />
      <BrowserRouter
        future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
      >
        <AuthProvider>
          <Routes>
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route
              path="/*"
              element={
                <AuthGuard>
                  {/* Scoped inside the auth boundary so the conversation list
                      is loaded fresh per signed-in session and torn down on
                      logout. Above it, the provider persisted across SPA
                      logout/login and never reloaded — showing the previous
                      session's stale chat titles until a manual page refresh
                      (and leaking the prior user's chats into the next login).
                      Toasts sit above it: the provider reports failed
                      loads, renames and deletes through them. */}
                  <ToastProvider>
                    <AiChatProvider conversationContext="studio">
                      <CommandPaletteProvider>
                        <LeaveGuardProvider>
                          <AppShell />
                        </LeaveGuardProvider>
                      </CommandPaletteProvider>
                    </AiChatProvider>
                  </ToastProvider>
                </AuthGuard>
              }
            />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
